import { z } from 'zod';

export const taskSchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'Title is required.'),
  description: z.string().min(1, 'Description is required.'),
  control_id: z.string().optional(),
  parent: z.number().nullable(),
  priority: z.number(),
});

export const taskWithChildrenSchema = taskSchema.extend({
  children: z.array(taskSchema),
});

export const formSchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'Template name must not be empty.'),
  subtasks: z.array(taskWithChildrenSchema),
});

export const newTemplateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});

export const newTaskSchema = newTemplateSchema.extend({
  description: z.string().min(1, 'Description is required'),
  template: z.number(),
  priority: z.number(),
});

export const newSubTaskSchema = newTaskSchema.extend({
  parent: z.number().nullable(),
});

export const templateResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  classification: z.number(),
});

export const createUpdateTemplateResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  subtasks: z.array(taskWithChildrenSchema),
});

export const processWorkflowSchema = z.object({
  id: z.number(),
  description: z.string(),
  is_draft: z.boolean(),
  name: z.string(),
  type: z.number(),
  tenant: z.number().nullable(),
});

export const attachmentItemSchema = z.object({
  id: z.number(),
  file_type: z.string(),
  original_file_name: z.string(),
  upload_finished_at: z.coerce.date(),
  uploaded_by: z.object({
    full_name: z.string(),
    id: z.number(),
  }).nullable(),
  url: z.string(),
  user_full_name: z.string().nullable(),
  user_type: z.number().nullable(),
  attachment_type: z.number().nullable(),
});

export const workflowTaskSchema = z.object({
  id: z.number(),
  name: z.string(),
  assignee_details: z.object({ id: z.number(), full_name: z.string() }),
  parent: z.number().nullable(),
  description: z.string(),
  status: z.number(),
  priority: z.number(),
  template: z.number(),
  control_id: z.string(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  created_by_details: z.object({ id: z.number(), full_name: z.string() }),
  formatted_due_date: z.coerce.date().nullable(),
  due_date: z.coerce.date().nullable(),
});

export const processWorkflowTaskSchema = workflowTaskSchema.extend({
  children: z.array(workflowTaskSchema),
});

export const workflowTaskCreateSchema = z.object({
  parent: z.number(),
  name: z.string(),
  description: z.string(),

});

export const workflowTaskCreateUpdateSchema = z.object({
  template: z.number(),
  parent: z.number().optional(),
  name: z.string(),
  description: z.string(),
  status: z.number().optional(),
  priority: z.number().optional(),
  assignee: z.number().optional(),
  due_date: z.coerce.date().optional(),
});


export const taskCommentSchema = z.object({
  id: z.number(),
  subtask: z.number(),
  author: z.string(),
  author_details: z.object({
    full_name: z.string(),
    id: z.number(),
  }),
  content: z.string(),
  tagged_users: z.array(z.number()),
  tagged_users_details: z.array(z.object({
    full_name: z.string(),
    id: z.number(),
  })),
  report_files_data: z.array(attachmentItemSchema),
  files: z.array(attachmentItemSchema),
  created_at: z.coerce.date(),
  file_ids: z.array(z.number()).optional(),
  report_files: z.array(z.number()).optional(),
});

export const filterSchema = z.object({
  status: z.object({
    TODO: z.boolean(),
    IN_PROGRESS: z.boolean(),
    UNDER_REVIEW: z.boolean(),
    COMPLETED: z.boolean(),
  }),
  assignee: z.array(z.number()),
  no_assignee: z.boolean(),
  is_assignee: z.boolean(),
  priority: z.object({
    CRITICAL: z.boolean(),
    HIGH: z.boolean(),
    MEDIUM: z.boolean(),
    LOW: z.boolean(),
  }),
  custom_date_value: z.object({
    from: z.date().nullable(),
    to: z.date().nullable(),
  }),
  custom_date: z.boolean(),
  overdue: z.boolean(),
  no_due_date: z.boolean(),
});




export type FilterFormFields = z.infer<typeof filterSchema>;
export type CreateUpdateTemplate = z.infer<
  typeof createUpdateTemplateResponseSchema
>;
export type ProcessTemplate = z.infer<typeof templateResponseSchema>;
export type NewTemplateField = z.infer<typeof newTemplateSchema>;
export type SubTask = z.infer<typeof taskSchema>;
export type Task = z.infer<typeof taskWithChildrenSchema>;
export type TemplateSchema = z.infer<typeof formSchema>;
export type NewTask = z.infer<typeof newTaskSchema>;
export type NewSubTask = z.infer<typeof newSubTaskSchema>;
export type ProcessWorkflow = z.infer<typeof processWorkflowSchema>;
export type ProcessWorkflowTask = z.infer<typeof processWorkflowTaskSchema>;
export type ProcessWorkflowSubTask = z.infer<typeof workflowTaskSchema>;
export type ProcessWorkflowTaskCreateUpdate = z.infer<typeof workflowTaskCreateUpdateSchema>;
export type TaskComment = z.infer<typeof taskCommentSchema>;
export type AttachmentItem = z.infer<typeof attachmentItemSchema>;
